import { useState, useRef, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import SearchBox from "./SearchBox";
import CrisisCircle from "./CrisisCircle";
import {
  SHRINK_NETWORK_URL,
  SHRINK_NETWORK_TEAL,
  networkUrl,
} from "@/lib/site";

const TOPICS: { to: string; label: string; desc: string }[] = [
  {
    to: "/panic-nervous-system",
    label: "Panic and the nervous system",
    desc: "Fight-or-flight, hyperventilation, why panic ends.",
  },
  {
    to: "/overthinking-rumination",
    label: "Overthinking and rumination",
    desc: "Replaying conversations, intrusive thoughts, mental loops.",
  },
  {
    to: "/work-performance",
    label: "Work and performance",
    desc: "High-functioning anxiety, burnout, perfectionism.",
  },
  {
    to: "/relationships-social",
    label: "Relationships and social anxiety",
    desc: "Social fear, attachment, texting anxiety.",
  },
  {
    to: "/treatment-education",
    label: "Treatment, explained",
    desc: "CBT, SSRIs, exposure therapy, finding care.",
  },
  {
    to: "/symptoms",
    label: "Symptoms (browse all)",
    desc: "Chest tightness, racing thoughts, the sensations most people notice first.",
  },
  {
    to: "/conditions",
    label: "Conditions (browse all)",
    desc: "GAD, panic disorder, OCD, social anxiety, PTSD, and phobias.",
  },
];

type GetHelpItem = {
  label: string;
  desc: string;
  to?: string;
  href?: string;
  arrow?: boolean;
};

const GET_HELP: GetHelpItem[] = [
  {
    to: "/resources/state-resources",
    label: "Find help in your state",
    desc: "State-by-state directory of clinicians, clinics, and crisis lines.",
  },
  {
    to: "/tools",
    label: "Free printable tools",
    desc: "14 printable worksheets, cards, and guides: panic cards, worry logs, sleep diaries, and more.",
  },
  {
    to: "/resources/just-diagnosed-with-anxiety",
    label: "Just diagnosed with anxiety: what happens next",
    desc: "What the diagnosis means and what to expect from the first weeks of treatment.",
  },
  {
    to: "/resources/supporting-a-partner-with-anxiety",
    label: "How to help someone with anxiety",
    desc: "A practical guide for partners, parents, and friends, without making it worse.",
  },
  {
    href: `${SHRINK_NETWORK_URL}/crisis/`,
    label: "Crisis support",
    desc: "Call or text 988 in the US. Network crisis resources.",
  },
  {
    to: "/resources",
    label: "All resources",
    desc: "The full directory of guides, reading, and state resources.",
    arrow: true,
  },
];

export default function Header() {
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const topicsRef = useRef<HTMLDivElement>(null);
  const topicsButtonRef = useRef<HTMLButtonElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Close on route change
  useEffect(() => {
    setTopicsOpen(false);
    setHelpOpen(false);
  }, [pathname]);

  // Close on outside click
  useEffect(() => {
    if (!topicsOpen && !helpOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (topicsRef.current && !topicsRef.current.contains(target)) {
        setTopicsOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(target)) {
        setHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [topicsOpen, helpOpen]);

  // Esc closes and returns focus to the open dropdown's button
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (topicsOpen) {
        setTopicsOpen(false);
        topicsButtonRef.current?.focus();
      }
      if (helpOpen) {
        setHelpOpen(false);
        helpButtonRef.current?.focus();
      }
    },
    [topicsOpen, helpOpen],
  );

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "nav-link is-active" : "nav-link";

  const topicsActive = TOPICS.some((t) => pathname.startsWith(t.to));
  const helpActive =
    pathname.startsWith("/resources") &&
    !pathname.startsWith("/resources/books");

  return (
    <header
      data-print-hide
      className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b"
      style={{
        borderColor: "hsl(var(--border))",
        boxShadow: scrolled
          ? "0 6px 24px -12px rgba(31, 41, 55, 0.25)"
          : "none",
        transition: "box-shadow 220ms ease",
      }}
      onKeyDown={onKeyDown}
    >
      <div
        aria-hidden="true"
        style={{ height: 3, background: "hsl(var(--brand))" }}
      />
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-5 pb-2">
          <div className="flex flex-col">
            <Link
              to="/"
              className="font-serif text-2xl font-semibold tracking-tight text-foreground hover:no-underline leading-none"
              data-testid="link-home"
            >
              AnxietyResource
              <span style={{ color: "hsl(var(--brand-text))", fontWeight: 600 }}>
                .org
              </span>
            </Link>
            <a
              href={networkUrl(SHRINK_NETWORK_URL, "header")}
              target="_blank"
              rel="noopener"
              className="mt-1 text-[11px] font-semibold tracking-wide hover:underline"
              style={{ color: SHRINK_NETWORK_TEAL }}
              data-testid="header-shrink-network-tag"
            >
              Part of The Shrink Network
            </a>
            <span
              className="mt-0.5 font-serif italic text-[9px] leading-none text-foreground"
              data-testid="header-site-tagline"
            >
              Anxiety. Understood.
            </span>
          </div>
          <span
            className="hidden sm:inline text-xs uppercase tracking-widest"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            an editorial publication
          </span>
          <div className="ml-auto flex items-center gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none">
              <SearchBox />
            </div>
            <CrisisCircle />
          </div>
        </div>
        <nav
          aria-label="Primary"
          className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-3 text-[14px]"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <NavLink
            to="/start-here"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-start-here"
          >
            Start Here
          </NavLink>

          {/* Topics: dropdown on >=md, accordion underneath on mobile */}
          <div ref={topicsRef} className="relative">
            <button
              ref={topicsButtonRef}
              type="button"
              aria-haspopup="true"
              aria-expanded={topicsOpen}
              aria-controls="topics-menu"
              onClick={() => {
                setTopicsOpen((v) => !v);
                setHelpOpen(false);
              }}
              className={
                topicsActive
                  ? "nav-link is-active inline-flex items-center gap-1"
                  : "nav-link inline-flex items-center gap-1"
              }
              style={{
                borderColor: topicsActive ? "hsl(var(--brand))" : undefined,
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
              }}
              data-testid="nav-topics"
            >
              Topics
              <span
                aria-hidden="true"
                style={{
                  fontSize: 10,
                  transform: topicsOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              >
                ▾
              </span>
            </button>

            {/* Desktop floating panel */}
            {topicsOpen && !isMobile && (
              <div
                id="topics-menu"
                role="menu"
                aria-label="Topics"
                className="absolute left-1/2 -translate-x-1/2 mt-3"
                style={{
                  width: 640,
                  maxWidth: "90vw",
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderTop: "3px solid hsl(var(--brand))",
                  borderRadius: "var(--radius)",
                  boxShadow: "0 12px 32px -8px rgba(0,0,0,0.18)",
                  zIndex: 50,
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 p-6">
                  {TOPICS.map((t) => (
                    <Link
                      key={t.to}
                      role="menuitem"
                      to={t.to}
                      className="block py-2 group"
                      onClick={() => setTopicsOpen(false)}
                      data-testid={`topics-menu-${t.to.slice(1)}`}
                    >
                      <span
                        className="block text-[15px] font-medium"
                        style={{ color: "hsl(var(--foreground))" }}
                      >
                        {t.label}
                      </span>
                      <span
                        className="block text-[13px] leading-snug"
                        style={{ color: "hsl(var(--muted-foreground))" }}
                      >
                        {t.desc}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/conditions"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-conditions"
          >
            Conditions
          </NavLink>
          <NavLink
            to="/maps"
            title="The Anxiety Maps: patterns you can see, treatments that move them."
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
              fontWeight: 600,
            })}
            data-testid="nav-maps"
          >
            Maps
          </NavLink>
          <NavLink
            to="/glossary"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-glossary"
          >
            Glossary
          </NavLink>

          {/* Get Help: dropdown on >=md, accordion underneath on mobile */}
          <div ref={helpRef} className="relative">
            <button
              ref={helpButtonRef}
              type="button"
              aria-haspopup="true"
              aria-expanded={helpOpen}
              aria-controls="get-help-menu"
              onClick={() => {
                setHelpOpen((v) => !v);
                setTopicsOpen(false);
              }}
              className={
                helpActive
                  ? "nav-link is-active inline-flex items-center gap-1"
                  : "nav-link inline-flex items-center gap-1"
              }
              style={{
                borderColor: helpActive ? "hsl(var(--brand))" : undefined,
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
              }}
              data-testid="nav-get-help"
            >
              Get Help
              <span
                aria-hidden="true"
                style={{
                  fontSize: 10,
                  transform: helpOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              >
                ▾
              </span>
            </button>

            {/* Desktop floating panel */}
            {helpOpen && !isMobile && (
              <div
                id="get-help-menu"
                role="menu"
                aria-label="Get Help"
                className="absolute left-1/2 -translate-x-1/2 mt-3"
                style={{
                  width: 360,
                  maxWidth: "90vw",
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderTop: "3px solid hsl(var(--brand))",
                  borderRadius: "var(--radius)",
                  boxShadow: "0 12px 32px -8px rgba(0,0,0,0.18)",
                  zIndex: 50,
                }}
              >
                <div className="grid grid-cols-1 gap-y-1 p-6">
                  {GET_HELP.map((item) =>
                    item.href ? (
                      <a
                        key={item.label}
                        role="menuitem"
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block py-2 group"
                        onClick={() => setHelpOpen(false)}
                        data-testid={`get-help-menu-crisis`}
                      >
                        <span
                          className="block text-[15px] font-medium"
                          style={{ color: "hsl(var(--foreground))" }}
                        >
                          {item.label}
                        </span>
                        <span
                          className="block text-[13px] leading-snug"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {item.desc}
                        </span>
                      </a>
                    ) : (
                      <Link
                        key={item.label}
                        role="menuitem"
                        to={item.to as string}
                        className="block py-2 group"
                        onClick={() => setHelpOpen(false)}
                        data-testid={`get-help-menu-${(item.to as string).slice(1)}`}
                      >
                        <span
                          className="block text-[15px] font-medium"
                          style={{ color: "hsl(var(--foreground))" }}
                        >
                          {item.label}
                          {item.arrow ? (
                            <span aria-hidden="true"> &rarr;</span>
                          ) : null}
                        </span>
                        <span
                          className="block text-[13px] leading-snug"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {item.desc}
                        </span>
                      </Link>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/anxiety-faq"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-faq"
          >
            FAQ
          </NavLink>
          <NavLink
            to="/resources/books"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-books"
          >
            Books
          </NavLink>
          <NavLink
            to="/about"
            className={linkClass}
            style={({ isActive }) => ({
              borderColor: isActive ? "hsl(var(--brand))" : undefined,
            })}
            data-testid="nav-about"
          >
            About
          </NavLink>
        </nav>

        {/* Mobile accordion: Topics */}
        {topicsOpen && isMobile && (
          <nav
            id="topics-menu-mobile"
            role="menu"
            aria-label="Topics"
            className="md:hidden pb-4"
          >
            <div
              className="rounded"
              style={{
                border: "1px solid hsl(var(--border))",
                borderTop: "3px solid hsl(var(--brand))",
                background: "hsl(var(--card))",
              }}
            >
              <ul className="list-none p-0 m-0">
                {TOPICS.map((t) => (
                  <li
                    key={t.to}
                    style={{ borderTop: "1px solid hsl(var(--border))" }}
                  >
                    <Link
                      role="menuitem"
                      to={t.to}
                      onClick={() => setTopicsOpen(false)}
                      className="block px-4 py-3"
                      data-testid={`topics-menu-mobile-${t.to.slice(1)}`}
                    >
                      <span
                        className="block text-[15px] font-medium"
                        style={{ color: "hsl(var(--foreground))" }}
                      >
                        {t.label}
                      </span>
                      <span
                        className="block text-[13px] leading-snug mt-0.5"
                        style={{ color: "hsl(var(--muted-foreground))" }}
                      >
                        {t.desc}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        )}

        {/* Mobile accordion: Get Help */}
        {helpOpen && isMobile && (
          <nav
            id="get-help-menu-mobile"
            role="menu"
            aria-label="Get Help"
            className="md:hidden pb-4"
          >
            <div
              className="rounded"
              style={{
                border: "1px solid hsl(var(--border))",
                borderTop: "3px solid hsl(var(--brand))",
                background: "hsl(var(--card))",
              }}
            >
              <ul className="list-none p-0 m-0">
                {GET_HELP.map((item) => (
                  <li
                    key={item.label}
                    style={{ borderTop: "1px solid hsl(var(--border))" }}
                  >
                    {item.href ? (
                      <a
                        role="menuitem"
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setHelpOpen(false)}
                        className="block px-4 py-3"
                        data-testid="get-help-menu-mobile-crisis"
                      >
                        <span
                          className="block text-[15px] font-medium"
                          style={{ color: "hsl(var(--foreground))" }}
                        >
                          {item.label}
                        </span>
                        <span
                          className="block text-[13px] leading-snug mt-0.5"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {item.desc}
                        </span>
                      </a>
                    ) : (
                      <Link
                        role="menuitem"
                        to={item.to as string}
                        onClick={() => setHelpOpen(false)}
                        className="block px-4 py-3"
                        data-testid={`get-help-menu-mobile-${(item.to as string).slice(1)}`}
                      >
                        <span
                          className="block text-[15px] font-medium"
                          style={{ color: "hsl(var(--foreground))" }}
                        >
                          {item.label}
                          {item.arrow ? (
                            <span aria-hidden="true"> &rarr;</span>
                          ) : null}
                        </span>
                        <span
                          className="block text-[13px] leading-snug mt-0.5"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {item.desc}
                        </span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
